import { Months } from '@constants/constants';
import { formatSingleDate } from './utils';

export interface MonthOption {
  value: string;
  label: string;
  month: string;
  year: string;
  selected?: boolean;
}

export interface MonthYear {
  month: string;
  year: string;
}

export interface DateRangeOption {
  label: string;
  value: string;
  selected?: boolean;
}

export class DateUtils {
  static getMonthIndex(monthName: string): number {
    return Months.findIndex(
      (m) => m.toLowerCase() === monthName.trim().toLowerCase(),
    );
  }

  static generateMonthOptions(count: number = 12): MonthOption[] {
    const now = new Date();
    const options: MonthOption[] = [];
    for (let i = 0; i < count; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = monthDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
      const month = (monthDate.getMonth() + 1).toString().padStart(2, '0');
      const year = monthDate.getFullYear().toString();
      options.push({
        label: monthLabel,
        value: `${month}, ${year}`,
        month: month,
        year: year,
        selected: i === 0,
      });
    }
    return options;
  }

  static getQuarterFromMonth(month: number): number {
    return Math.ceil(month / 3);
  }

  static setMonthAndYear(selectedOption: string): MonthYear {
    const [monthValue, yearValue] = selectedOption?.split(' ') || [];
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
    return { month: formattedMonth, year: yearValue };
  }

  static getMonthName(monthIndex: number): string {
    return new Date(2000, monthIndex, 1).toLocaleString('default', {
      month: 'long',
    });
  }

  static formatDate = (date: Date | null): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * Formats a Date or date string to YYYY-MM-DD format without timezone conversion
   * @param date - Date object, date string in YYYY-MM-DD format, or any date value
   * @returns Formatted date string in YYYY-MM-DD format
   */
  static formatDateToString(date: Date | string | any): string {
    if (!date) return '';
    
    let parsedDate: Date;
    if (date instanceof Date) {
      parsedDate = date;
    } else if (typeof date === 'string') {
      // Parse string to avoid timezone conversion
      const [year, month, day] = date.split('-').map(Number);
      if (year && month && day) {
        parsedDate = new Date(year, month - 1, day);
      } else {
        parsedDate = new Date(date);
      }
    } else {
      parsedDate = new Date(date);
    }
    
    // Format without timezone conversion
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Parses a date string to a Date object without timezone conversion
   * Useful for Angular Material datepickers which expect Date objects
   * @param dateString - Date string in YYYY-MM-DD format
   * @returns Date object set to local timezone
   */
  static parseLocalDate(dateString: string | Date | any): Date {
    if (!dateString) return new Date('');
    
    // If already a Date object, return it
    if (dateString instanceof Date) {
      return dateString;
    }

    // Parse string to avoid timezone conversion
    const [year, month, day] = String(dateString).split('-').map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day);
    }
    
    return new Date(dateString);
  }

  static generateDateRangeOptions(): DateRangeOption[] {
    return [
      { label: 'Last Month', value: 'Last Month', selected: true },
      { label: 'Last 3 Months', value: 'Last 3 Months', selected: false },
      { label: 'Last 6 Months', value: 'Last 6 Months', selected: false },
      { label: 'Last 9 Months', value: 'Last 9 Months', selected: false },
      { label: 'Last 12 Months', value: 'Last 12 Months', selected: false },
      { label: 'Custom Date Range', value: 'Custom Date Range', selected: false }
    ];
  }
}

export function getMonthDateRange(
  monthYear: string,
  dateFormat: string,
): { startDate: string; endDate: string } {
  // Handle undefined or null input
  if (!monthYear || typeof monthYear !== 'string') {
    const now = new Date();
    monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
  }

  const parts = monthYear.split(/[, -]+/).filter((part) => part && part.trim());

  // Ensure we have at least month and year
  if (parts.length < 2) {
    const now = new Date();
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = now.getFullYear().toString();
    return getMonthDateRange(`${currentMonth}-${currentYear}`, dateFormat);
  }

  const [selectedMonth, selectedYearRaw] = parts;
  const selectedYear =
    selectedYearRaw?.trim() || new Date().getFullYear().toString();

  const currentStartDate = `${selectedYear}-${selectedMonth}-01`;

  // Always use last day of the month as end date
  const daysInMonth = new Date(+selectedYear, +selectedMonth, 0).getDate();
  const currentEndDate = `${selectedYear}-${selectedMonth}-${daysInMonth}`;

  return {
    startDate: formatSingleDate(currentStartDate, dateFormat),
    endDate: formatSingleDate(currentEndDate, dateFormat),
  };
}
